import React from "react";
import CommonGrid from "./CommonGrid";
import { ColumnType } from "../../../../components/Grid/constants";
import { FilterOperation } from "../../../../components/Grid/dataProvider";
import {
  StatementTypeGrid,
  StatementStateInternal,
  StatementStateFilterItems,
  Visibility,
  InitiativeState,
  AppealsType,
  InspectionTypeEnum,
  InspectionResultEnum,
  InspectionResult,
  InspectionResultSymbol, RequestState, RequestStateRaw
} from "../../enum";
import {showDegrees, formatCurrency, showProp, displayHowManyDaysLeft} from "../../CroUtils";
import { FormattedDate } from "react-intl";

const o = (field) => (obj) => obj ? obj[field] : null;

const enumNameMapper = (field) =>
    typeof field === "object" && field ? field.name : field || "";

const exportValueEnumMapper = (row, column) => {
  const fieldValue = column.displayField ? row[column.displayField] : null;
  if (fieldValue && column.enum) {
    const enumItem = column.enum.find((item) => item.id === fieldValue);
    return enumItem ? enumItem.label : "";
  }
  return "";
}

const OFFICIAL_COLUMNS = [
  {
    filterable: true,
    field: "lastName",
    label: "Příjmení",
    type: ColumnType.TEXT,
    width: "7%",
  },
  {
    filterable: true,
    field: "firstName",
    label: "Jméno",
    type: ColumnType.TEXT,
    width: "6%",
  },
  {
    sortable: false,
    field: "degrees",
    label: "Tituly",
    type: ColumnType.JSX,
    jsx: (row) =>
      row.titleBefore || row.titleAfter
        ? showDegrees(row.titleBefore, row.titleAfter)
        : row.official
              ? showDegrees(row.official.titleBefore, row.official.titleAfter)
              : "",
    width: "5%",
  },
  {
    filterable: true,
    field: "birthDate",
    label: "Datum narození",
    type: ColumnType.DATE,
    width: "7%",
  },
  {
    filterable: true,
    field: "ovmName",
    label: "Podpůrný orgán",
    type: ColumnType.TEXT,
    width: "8%",
  },
  {
    filterable: true,
    field: "concatenatedWorkingPositions",
    label: "Funkce",
    type: ColumnType.TEXT,
    width: "20%",
    maxLength: 80,
  },
  {
    filterable: true,
    field: "concatenatedWorkingPositionOrganizations",
    label: "Organizace",
    type: ColumnType.TEXT,
    width: "10%",
  },
];

const OFFICIAL_IDENTITY_COLUMNS = OFFICIAL_COLUMNS.map((col) => {
  return {
    ...col,
    mapper: o(col.field),
    displayField: "official",
  };
});

// ── Funkcionáři ──────────────────────────────────────────────────────────────

export const FUNKCIONARI_COLUMNS = [
  ...OFFICIAL_COLUMNS,
  {
    field: "workingFrom",
    label: "Začátek funkcí",
    type: ColumnType.DATE,
    width: "6%",
  },
  {
    field: "workingTo",
    label: "Konec funkcí",
    type: ColumnType.DATE,
    width: "6%",
  },
  {
    filterable: true,
    field: "databox",
    label: "Datová schránka",
    type: ColumnType.TEXT,
    width: "7%",
  },
  {
    filterable: true,
    filterBy: { type: ColumnType.YESNO, field: "wpVisibility", values: {
      "true": { operation: FilterOperation.EQ, value: "Veřejný" },
      "false": { operation: FilterOperation.NEQ, value: "Veřejný" }
    }},
    field: "wpVisibility",
    label: "Viditelnost",
    type: ColumnType.TEXT,
    width: "6%",
  },
  {
    filterable: true,
    sortable: false,
    field: "active",
    label: "Aktivní",
    type: ColumnType.YESNO,
    width: "4%",
  },
  {
    filterable: true,
    sortable: false,
    field: "checked",
    label: "Zkontrolován",
    type: ColumnType.YESNO,
    width: "5%",
  },
  {
    filterable: true,
    filterBy: { type: ColumnType.NUMBER, field: "statementsReferencesCount" },
    sortable: false,
    field: "statementsCount",
    label: "Podaná oznámení",
    type: ColumnType.NUMBER,
    width: "6%",
  },
  {
    filterable: true,
    filterBy: { type: ColumnType.YESNO, field: "appealDate", values: {
        "true": { operation: FilterOperation.NOT_NULL },
        "false": { operation: FilterOperation.IS_NULL },
    }},
    field: "appealDate",
    label: "Byla odeslána výzva",
    type: ColumnType.JSX,
    jsx: (row) => {
      const d = row.appealDate;
      return d ? <FormattedDate value={d} /> : <span>NE</span>;
    },
    width: "7%",
  },
  {
    sortable: false,
    filterable: true,
    filterBy: { type: ColumnType.YESNO, field: "concatenatedEsmFunctions", values: {
      "true": { operation: FilterOperation.NOT_NULL },
      "false": { operation: FilterOperation.IS_NULL }
    }},
    field: "hasEsmFunction",
    displayField: "official",
    label: "Má funkci dle ESM",
    type: ColumnType.JSX,
    jsx: (row) => {
      return row.concatenatedEsmFunctions ? "ANO" : "NE";
    },
    width: "5%",
  },
  {
    filterable: true,
    field: "concatenatedEsmFunctions",
    label: "Funkce dle ESM",
    type: ColumnType.TEXT,
    width: "7%",
  },
];

export const StatisticsFunkcionariGrid = (props) => (
  <CommonGrid {...props} columns={FUNKCIONARI_COLUMNS} />
);

export const FUNKCIONARI_OZNAMENI_COLUMNS = [
    ...OFFICIAL_COLUMNS,
  {
    field: "workingPeriod",
    label: "Období",
    type: ColumnType.JSX,
    width: "15%",
    jsx: (row) => {
      return <div>
        {row.workingFrom && <FormattedDate value={row.workingFrom} />} - {row.workingTo && <FormattedDate value={row.workingTo} />}
      </div>;
    },
  },
  {
    filterable: true,
    field: "databox",
    label: "Datová schránka",
    type: ColumnType.TEXT,
    width: "7%",
  },
  {
    filterable: true,
    filterBy: { type: ColumnType.YESNO, field: "wpVisibility", values: {
        "true": { operation: FilterOperation.EQ, value: "Veřejný" },
        "false": { operation: FilterOperation.NEQ, value: "Veřejný" }
      }},
    field: "wpVisibility",
    label: "Viditelnost",
    type: ColumnType.TEXT,
    width: "6%",
  },
  {
    filterable: true,
    sortable: false,
    field: "active",
    label: "Aktivní",
    type: ColumnType.CHECK,
    width: "4%",
  },
  {
    filterable: true,
    sortable: false,
    field: "checked",
    label: "Zkontrolován",
    type: ColumnType.YESNO,
    width: "5%",
  },
  {
    filterable: true,
    filterBy: { type: ColumnType.YESNO, field: "appealDate", values: {
        "true": { operation: FilterOperation.NOT_NULL },
        "false": { operation: FilterOperation.IS_NULL },
      }},
    field: "appealDate",
    label: "Byla odeslána výzva",
    type: ColumnType.JSX,
    jsx: (row) => {
      const d = row.appealDate;
      return d ? <FormattedDate value={d} /> : <span>NE</span>;
    },
    width: "7%",
  },
]

// ── Funkcionáři - Chybějící oznámení ──────────────────────────────────────────────────────────────────
export const FUNKCIONARI_CHYBEJICI_OZNAMENI_COLUMNS = [
  ...FUNKCIONARI_OZNAMENI_COLUMNS
]

export const StatisticsFunkcionariChybejiciOznameniGrid = (props) => (
    <CommonGrid {...props} columns={FUNKCIONARI_CHYBEJICI_OZNAMENI_COLUMNS} />
);

// ── Funkcionáři - Neúplné oznámení ──────────────────────────────────────────────────────────────────
export const FUNKCIONARI_NEUPLNE_OZNAMENI_COLUMNS = [
  ...FUNKCIONARI_OZNAMENI_COLUMNS
]

export const StatisticsFunkcionariNeuplneOznameniGrid = (props) => (
    <CommonGrid {...props} columns={FUNKCIONARI_NEUPLNE_OZNAMENI_COLUMNS} />
);

// ── Funkcionáři - Opožděné oznámení ──────────────────────────────────────────────────────────────────
export const FUNKCIONARI_OPOZDENE_OZNAMENI_COLUMNS = [
  ...FUNKCIONARI_OZNAMENI_COLUMNS
]

export const StatisticsFunkcionariOpozdeneOznameniGrid = (props) => (
    <CommonGrid {...props} columns={FUNKCIONARI_OPOZDENE_OZNAMENI_COLUMNS} />
);

// ── Funkcionáři - Včasné oznámení ──────────────────────────────────────────────────────────────────
export const FUNKCIONARI_VCASNE_OZNAMENI_COLUMNS = [
  ...FUNKCIONARI_OZNAMENI_COLUMNS
]

export const StatisticsFunkcionariVcasneOznameniGrid = (props) => (
    <CommonGrid {...props} columns={FUNKCIONARI_VCASNE_OZNAMENI_COLUMNS} />
);

// ── Oznámení ──────────────────────────────────────────────────────────────────
export const OZNAMENI_COLUMNS = [
  ...OFFICIAL_IDENTITY_COLUMNS,
  {
    sortable: false,
    filterable: true,
    field: "isOfficialReleased",
    label: "Uvolněný veřejný funkcionář",
    type: ColumnType.YESNO,
    width: "6%",
  },
  {
    field: "type.name",
    displayField: "type",
    label: "Druh oznámení",
    type: ColumnType.ENUM,
    enum: StatementTypeGrid,
    mapper: enumNameMapper,
    exportValue: exportValueEnumMapper,
    width: "10%",
  },
  {
    field: "state.name",
    displayField: "state",
    label: "Stav oznámení",
    type: ColumnType.ENUM,
    enum: StatementStateInternal,
    mapper: enumNameMapper,
    exportValue: exportValueEnumMapper,
    filterItems: StatementStateFilterItems,
    width: "6%",
  },
  {
    field: "fromDate",
    label: "Období od",
    type: ColumnType.DATE,
    width: "5%",
  },
  {
    field: "toDate",
    label: "Období do",
    type: ColumnType.DATE,
    width: "5%",
  },
  {
    field: "creationDate",
    label: "Datum podání",
    type: ColumnType.DATE,
    width: "6%",
  },
  {
    field: "lastSaved",
    label: "Datum poslední změny",
    type: ColumnType.DATE,
    width: "6%",
  },
  {
    field: "visibility.name",
    displayField: "visibility",
    label: "Viditelnost",
    type: ColumnType.ENUM,
    enum: Visibility,
    mapper: enumNameMapper,
    exportValue: exportValueEnumMapper,
    width: "6%",
  },
  {
    sortable: false,
    filterable: true,
    filterBy: { type: ColumnType.YESNO, field: "deleted", values: {
        "true": { operation: FilterOperation.NOT_NULL },
        "false": { operation: FilterOperation.IS_NULL },
    }},
    field: "deleted",
    label: "Smazáno",
    type: ColumnType.JSX,
    jsx: (row) => (row.deleted ? "ANO" : "NE"),
    width: "4%",
  },
  {
    sortable: false,
    filterable: true,
    filterBy: { type: ColumnType.YESNO, field: "bankAccountBalance", values: {
        "true": { operation: FilterOperation.GT, value: "0" },
        "false": { operation: FilterOperation.LTE, value: "0" },
    }},
    field: "hasCashOnBankAccount",
    label: "Peněžní prostředky na účtu",
    type: ColumnType.JSX,
    jsx: (row) => (row.bankAccountBalance ? "ANO" : "NE"),
    width: "6%",
  },
  {
    sortable: false,
    filterable: true,
    filterBy: { type: ColumnType.NUMBER, field: "bankAccountBalance" },
    field: "bankAccountBalance",
    label: "Peněžní prostředky - částka",
    type: ColumnType.JSX,
    jsx: (row) => (row.bankAccountBalance ?
        formatCurrency(row.bankAccountBalance, 'Kč', 0, 2)
        : ""),
    width: "6%",
  },
  {
    sortable: false,
    filterable: true,
    field: "realtiesEnabled",
    label: "Věci nemovité",
    type: ColumnType.YESNO,
    width: "5%",
  },
  {
    sortable: false,
    filterable: true,
    field: "naturalPersonBusinessesEnabled",
    label: "Živnostenské oprávnění",
    type: ColumnType.YESNO,
    width: "5%",
  },
  {
    sortable: false,
    filterable: true,
    filterBy: { type: ColumnType.NUMBER, field: "declaredIncomes" },
    field: "declaredIncomes",
    label: "Uvedené příjmy",
    type: ColumnType.JSX,
    jsx: (row) => (row.declaredIncomes ?
        formatCurrency(row.declaredIncomes, 'Kč', 0, 2)
        : ""),
    width: "6%",
  },
  {
    sortable: false,
    filterable: true,
    field: "isReleased",
    label: "Uvolněnost",
    type: ColumnType.YESNO,
    width: "5%",
  },
  {
    sortable: false,
    filterable: true,
    field: "isIncompleteStatement",
    label: "Neúplné oznámení",
    type: ColumnType.YESNO,
    width: "5%",
  },
  {
    sortable: false,
    filterable: true,
    filterBy: { type: ColumnType.YESNO, field: "late" },
    field: "isLateStatement",
    label: "Opožděné oznámení",
    type: ColumnType.YESNO,
    width: "5%",
  },
];

export const StatisticsOznameniGrid = (props) => (
  <CommonGrid {...props} columns={OZNAMENI_COLUMNS} />
);

// ── Výzvy ─────────────────────────────────────────────────────────────────────

export const VYZVY_COLUMNS = [
  ...OFFICIAL_IDENTITY_COLUMNS,
  {
    field: "type.name",
    displayField: "type",
    label: "Důvod",
    type: ColumnType.ENUM,
    enum: AppealsType,
    filterItems: AppealsType,
    filterBy: { type: ColumnType.ENUM, field: 'type.id', values:
          Object.keys(AppealsType).reduce((acc, key) => {
            acc[key] = { operation: FilterOperation.EQ, value: key };
            return acc;
          }, {})
    },
    mapper: enumNameMapper,
    exportValue: exportValueEnumMapper,
    width: "15%",
  },
  {
    field: "created",
    label: "Datum podání výzvy",
    type: ColumnType.DATETIME,
    width: "10%",
  }
];

export const StatisticsVyzvyGrid = (props) => (
  <CommonGrid {...props} columns={VYZVY_COLUMNS} />
);

// ── Kontroly ──────────────────────────────────────────────────────────────────

export const KONTROLY_COLUMNS = [
  ...OFFICIAL_IDENTITY_COLUMNS,
  {
    field: 'type.id',
    displayField: 'type',
    filterItems: InspectionTypeEnum,
    label: 'Typ kontroly',
    enum: InspectionTypeEnum,
    type: ColumnType.ENUM,
    exportValue: exportValueEnumMapper,
    width: '15%'
  },
  {
    field: 'inspectionDate',
    label: 'Datum kontroly',
    type: ColumnType.DATE,
    width: '10%'
  },
  {
    field: 'result.id',
    displayField: 'result',
    sortable: true,
    filterable: true,
    filterItems: InspectionResultEnum,
    filterBy: { type: ColumnType.ENUM, field: 'result.name', values:
          Object.keys(InspectionResult).reduce((acc, key) => {
            acc[key] = { operation: FilterOperation.EQ, value: key };
            return acc;
          }, {})
    },
    label: 'Výsledek kontroly',
    type: ColumnType.JSX,
    jsx: (row) => {
      const resultSymbol = InspectionResultSymbol[row.result] ? InspectionResultSymbol[row.result] : '';
      const styles = {
        fontSize: 15,
        paddingLeft: 5,
        fontWeight: 600,
        color: row.result === InspectionResult.OK ? '#006505' : row.result === InspectionResult.PROBLEM ? '#ab0c00' : 'black'
      };
      return <span style={styles}>{resultSymbol}</span>;
    },
    width: '5%'
  },
  {
    field: 'forwardedTo',
    label: 'Předáno přestupkovému orgánu (ORP)',
    type: ColumnType.TEXT,
    width: '20%'
  }
];

export const StatisticsKontrolyGrid = (props) => (
  <CommonGrid {...props} columns={KONTROLY_COLUMNS} />
);

// ── Podpůrné orgány ───────────────────────────────────────────────────────────

export const PODPURNE_ORGANY_COLUMNS = [
  {
    filterable: true,
    field: "ic",
    label: "IČO",
    type: ColumnType.TEXT,
    width: "8%",
  },
  {
    filterable: true,
    field: "subject",
    label: "Název",
    type: ColumnType.TEXT,
    width: "20%",
  },
  {
    filterable: true,
    field: "username",
    label: "Přihlašovací jméno",
    type: ColumnType.TEXT,
    width: "12%",
  },
  {
    filterable: true,
    field: "databox",
    label: "Datová schránka",
    type: ColumnType.TEXT,
    width: "10%",
  },
  {
    field: "created",
    label: "Vytvořen",
    type: ColumnType.DATE,
    width: "8%",
  },
  {
    field: "officialsCount",
    label: "Počet funkcionářů",
    type: ColumnType.NUMBER,
    width: "6%",
  },
  {
    sortable: false,
    filterable: true,
    filterBy: { type: ColumnType.YESNO, field: "officialsCount", values: {
        "true": { operation: FilterOperation.LTE, value: "0" },
        "false": { operation: FilterOperation.GT, value: "0" },
    }},
    field: "officialsCount",
    label: "Bez funkcionářů",
    type: ColumnType.JSX,
    jsx: (row) => (!row.officialsCount || row.officialsCount === 0 ? "ANO" : "NE"),
    exportValue: (row) => (!row.officialsCount || row.officialsCount === 0 ? "ANO" : "NE"),
    width: "6%",
  },
];

export const StatisticsPodpurneOrganyGrid = (props) => (
  <CommonGrid {...props} columns={PODPURNE_ORGANY_COLUMNS} />
);

// ── Žádosti o nahlížení ───────────────────────────────────────────────────────

export const ZADOSTI_COLUMNS = [
  {
    field: "firstName",
    displayField: "person",
    label: "Jméno žadatele",
    type: ColumnType.TEXT,
    mapper: (field) => (field ? field.firstName : ""),
    width: "12%",
  },
  {
    field: "lastName",
    displayField: "person",
    label: "Příjmení žadatele",
    type: ColumnType.TEXT,
    mapper: (field) => (field ? field.lastName : ""),
    width: "12%",
  },
  {
    sortable: false,
    filterable: true,
    filterBy: { type: ColumnType.TEXT, field: "officialsNames", values: {} },
    field: "officialFullName",
    displayField: "officials",
    label: "Funkcionáři",
    type: ColumnType.TEXT,
    mapper: (field) =>
        `${field && field[0] ? field[0]["firstName"] : ""} ${showProp(field, "lastName")}`,
    width: "24%",
  },
  {
    field: "state.name",
    displayField: "state",
    label: "Stav",
    enum: RequestState,
    type: ColumnType.ENUM,
    mapper: (field) =>
        typeof field === "object" && field ? field.name : field || "",
    width: "8%",
  },
  {
    sortable: false,
    field: "period",
    label: "Lhůta",
    type: ColumnType.JSX,
    jsx: (row) =>
        displayHowManyDaysLeft(row.created, row.state === RequestStateRaw.NEW),
    width: "8%",
  },
  {
    field: "created",
    label: "Vytvořeno",
    type: ColumnType.DATE,
    width: "8%",
  },
  {
    field: "assignedTo.name",
    displayField: "assignedTo",
    label: "Vyřizuje",
    type: ColumnType.TEXT,
    mapper: function(field) { return field ? field.name : ""; },
    width: "10%",
  },
  {
    field: "assignedDate",
    label: "Datum přidělení",
    type: ColumnType.DATE,
    width: "8%",
  },
  {
    field: "processingDate",
    label: "Vyřešeno",
    type: ColumnType.DATE,
    width: "8%",
  }
];

export const StatisticsZadostiGrid = (props) => (
    <CommonGrid {...props} columns={ZADOSTI_COLUMNS} />
);

// ── Podněty k prošetření ──────────────────────────────────────────────────────

export const PODNETY_COLUMNS = (props) => [
  ...OFFICIAL_IDENTITY_COLUMNS,
  {
    field: "state.name",
    displayField: "state",
    label: "Stav",
    type: ColumnType.ENUM,
    enum: InitiativeState,
    filterItems: InitiativeState,
    filterBy: { type: ColumnType.ENUM, field: 'state.id', values:
          Object.keys(InitiativeState).reduce((acc, key) => {
            acc[key] = { operation: FilterOperation.EQ, value: key };
            return acc;
          }, {})
    },
    mapper: enumNameMapper,
    exportValue: exportValueEnumMapper,
    width: "7%",
  },
  {
    filterable: true,
    field: "processingDate",
    label: "Vyřešeno",
    type: ColumnType.DATE,
    width: "7%",
  },
  {
    sortable: false,
    filterable: true,
    field: "anonymous",
    label: "Podáno anonymně",
    type: ColumnType.YESNO,
    width: "5%",
  },
  {
    filterable: true,
    field: "personFullName",
    label: "Jméno a příjmení podatele",
    type: ColumnType.TEXT,
    width: "8%",
  },
  {
    filterable: true,
    filterItems: props.forwardingReasons.map((reason) => ({
      id: reason.id,
      label: reason.name,
    })),
    filterBy: { type: ColumnType.ENUM, field: "concatenatedForwardingReasons", values:
      props.forwardingReasons.reduce((acc, reason) => {
        acc[reason.id] = { operation: FilterOperation.CONTAINS, value: reason.name };
        return acc;
      }, {})
    },
    field: "concatenatedForwardingReasons",
    label: "Důvod postoupení",
    type: ColumnType.TEXT,
    width: "8%",
  },
  {
    filterable: true,
    field: "forwardedDate",
    label: "Datum postoupení",
    type: ColumnType.DATE,
    width: "6%",
  },
  {
    filterable: true,
    field: "sendToOrganization",
    label: "Kam postoupeno",
    type: ColumnType.TEXT,
    width: "7%",
  },
  {
    sortable: false,
    filterable: true,
    field: "isRejected",
    label: "Zamítnuto",
    type: ColumnType.YESNO,
    width: "4%",
  },
  {
    filterable: true,
    field: "rejectText",
    label: "Důvod zamítnutí",
    type: ColumnType.TEXT,
    width: "8%",
  },
];

export const StatisticsPodnetyGrid = (props) => (
  <CommonGrid {...props} columns={PODNETY_COLUMNS(props)} />
);
